import { formatChartNumber } from '../charts/numberFormat.js'

/**
 * RED-317 PR 3d-B — where a retyped cell of an embedded Excel object goes in
 * its preview: a port of App\Services\Docx\Studio\Objects\ExcelObjectSheet
 * (display, alignment, place) and FontAdvances::pixels, so the canvas draws
 * the text exactly where the compiler writes it into the regenerated EMF.
 * The font table is the server's (the payload's `objects.fonts`); shared
 * vectors: tests/Fixtures/objects/layout-vectors.json.
 */

const tables = new WeakMap()

/** The table's runs decoded into one Map per face and style: code point → units. */
function decoded(fonts) {
  let maps = tables.get(fonts)
  if (!maps) {
    maps = new Map()
    tables.set(fonts, maps)
  }
  return maps
}

function advanceMap(fonts, face, style) {
  const maps = decoded(fonts)
  const key = `${face}|${style}`
  if (!maps.has(key)) {
    const map = new Map()
    for (const [first, units] of fonts.faces?.[face]?.[style] ?? []) units.forEach((value, offset) => map.set(first + offset, value))
    maps.set(key, map)
  }
  return maps.get(key)
}

const FACES = { calibri: 'calibri', carlito: 'calibri' }

export function fontStyle(bold, italic) {
  if (bold && italic) return 'boldItalic'
  if (bold) return 'bold'
  return italic ? 'italic' : 'regular'
}

/** Each character's advance in device pixels (FontAdvances::pixels), or null when one cannot be measured. */
export function pixels(fonts, font, text) {
  const face = FACES[String(font.face ?? '').trim().toLowerCase()]
  if (!face || !fonts) return null
  const map = advanceMap(fonts, face, fontStyle(font.bold, font.italic))
  const out = []
  for (const char of String(text)) {
    const units = map.get(char.codePointAt(0))
    if (units === undefined) return null
    out.push(Math.round((units * font.em) / fonts.unitsPerEm))
  }
  return out
}

/** General alignment resolved by the value: numbers right, texts left. */
export function alignment(align, numeric) {
  return align === 'general' ? (numeric ? 'right' : 'left') : align
}

/** The text a value shows (ExcelObjectSheet::display): a number in its format with the object's grouping, a text as it is. */
export function display(value, format, group) {
  if (value === null || value === undefined) return ''
  if (typeof value === 'string') return value
  const code = format === null || format === undefined || format === '@' ? 'General' : format
  return formatChartNumber(value, code).replaceAll(' ', group)
}

/**
 * The runs' x and advances by the cell's alignment over their whole width
 * (ExcelObjectSheet::place); null when a character cannot be measured or
 * the alignment has no padding.
 */
export function place(fonts, cell, runs, pads, numeric) {
  const measured = []
  let total = 0
  for (const run of runs) {
    const dx = pixels(fonts, run.font, run.text)
    if (!dx) return null
    const width = dx.reduce((sum, value) => sum + value, 0)
    measured.push({ dx, width })
    total += width
  }
  const [x0, , x1] = cell.box
  const align = alignment(cell.align, numeric)
  const pad = pads[align]
  if (pad === null || pad === undefined) return null
  let x
  if (align === 'right') x = x1 - pad - total
  else if (align === 'center') x = x0 + pad + Math.floor((x1 - x0 - total) / 2)
  else x = x0 + pad
  return measured.map((run) => {
    const out = { x, dx: run.dx, width: run.width }
    x += run.width
    return out
  })
}
